import React from "react";
import { styled } from "@linaria/react";
import Marker from "@ui/Highcharts/ui/Marker";
import testSerieData from "@ui/Highcharts/lib/testSerieData";
import {
  QUANTITY_FLAG_SERIE_ID,
  MARKER_NO_VALUE,
  MARKER_SVG_INVALID,
  MARKER_SVG_NO_VALUE_INVALID,
} from "../series/getQuantityFlagSerie";
import { color, fonts } from "@ui";
import type { ChartType, Interval } from "../../useNoDataIntervals";
import t from "../../../../i18n";
import Highcharts from "highcharts";
import { MARKERS_VISIBLE_EVENT } from "@ui/Highcharts/plugins/improveDrawPoints";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import {
  ANALYSIS_FLAGS_SERIE_ID,
  flagColors,
  flagLineColors,
} from "../series/getAnalysisFlagSerie";
import type { AnalysisFlag, AnalysisFlagMetadata } from "../PointMetadata";
import { SIGMA_AREA_SERIE_ID } from "../../Symptoms/get2SigmaSerie";
import type { Analysis } from "../../../../api/Analysis";
import { ALLDATA_FLAGS_SERIE_ID, flagColorsAllData } from "../series/getAllDataFlagSerie";
import { usePublicationFlagDisplay } from "../../Analysis/TogglePublicationDisplay";
import { computeGroupBreaks } from "../hooks/useGroupBreaks";
import { INVALID_QUANTITY_FLAG_SERIE_ID } from "../series/getInvalidQuantityFlagSerie";
import { css } from "@linaria/core";
import { colors } from "@ui/Theme";

export const commonLegendactiveSetup = css`
  font: ${fonts.body3};
  color: ${colors.carbon800};
`;

const Flag = styled.div<{ color: string; stroke?: string }>`
  background-color: ${props => props.color};
  border: 1px solid ${props => props.stroke ?? color.hydrogen};
  opacity: 1;
  height: 9px;
  width: 15px;
`;

const IntercycleZone = styled.div`
  background-color: ${color.titanium100};
  border-right: 4px solid ${color.titanium200};
  height: 15px;
  width: 15px;
`;

const OutOfFaultEventZone = styled.div`
  background-color: ${color.shadowColor};
  height: 15px;
  width: 15px;
`;

const ImgFlag = styled.img`
  width: 12px;
  height: 12px;
`;

const SigmaZone = styled.div`
  background-color: ${color.chlorine100};
  height: 15px;
  width: 15px;
`;

const Stripe = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="15px" height="15px" {...props}>
    <defs>
      <pattern
        id="pattern_AD6C9G"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(45)"
      >
        <line x1="0" y="0" x2="0" y2="26" stroke={color.titanium100} strokeWidth="2.5" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#pattern_AD6C9G)" opacity="1" />
  </svg>
);

function hasFlag(
  serie:
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesFlagsOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesScatterOptions,
  flag: AnalysisFlag,
  period: Period,
) {
  return testSerieData(serie, period, p => (p.custom as AnalysisFlagMetadata).flagType === flag);
}

const trueFun = () => true;

interface P {
  chartRef: React.RefObject<Highcharts.Chart>;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesFlagsOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesScatterOptions
  )[];
  period: Period;
  analyses: Analysis[];
  noDataIntervals: Interval[];
  chartType: ChartType;
  allAnalysesMode?: boolean;
  projectId: string;
  defaultFaultEventPeriod?: Period;
}

function CommonLegend({
  series,
  period,
  analyses,
  chartType,
  noDataIntervals,
  chartRef,
  allAnalysesMode = false,
  projectId,
  defaultFaultEventPeriod,
}: P) {
  const [markerEnabled, setMarkerEnabled] = React.useState(false);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("Missing chart");
    }
    const xAxis = chart.xAxis[0];
    if (!xAxis || xAxis.options.type !== "datetime") throw new Error("Wrong xAxis");
    Highcharts.addEvent(xAxis, MARKERS_VISIBLE_EVENT, ({ value }: { value: boolean }) => {
      setMarkerEnabled(value);
    });
  }, [chartRef]);

  const groupBreaks = React.useMemo(() => computeGroupBreaks(analyses), [analyses]);
  const visibleGroups = React.useMemo(
    () =>
      groupBreaks.filter(
        groupBreak =>
          (period.from === null || period.from < groupBreak.to) &&
          (period.to === null || groupBreak.from < period.to),
      ),
    [groupBreaks, period],
  );

  const visibleNoDataIntervals = React.useMemo(
    () =>
      noDataIntervals.filter(
        inter =>
          (period.from === null || period.from < inter.to) &&
          (period.to === null || inter.from < period.to),
      ),
    [noDataIntervals, period],
  );

  const faultEventBandsAreVisible = React.useMemo(() => {
    if (defaultFaultEventPeriod) {
      return defaultFaultEventPeriod.from > period.from || defaultFaultEventPeriod.to < period.to;
    }
    return false;
  }, [defaultFaultEventPeriod, period.from, period.to]);

  const quantityFlagSerie = series.find(s => s.id === QUANTITY_FLAG_SERIE_ID);
  const hasQuantityFlag = quantityFlagSerie && testSerieData(quantityFlagSerie, period, trueFun);
  const hasMissingValueQuantityFlag = hasQuantityFlag
    ? ((quantityFlagSerie?.data ?? []) as Highcharts.PointOptionsObject[]).some(
        s => s.marker?.symbol === `url(${MARKER_NO_VALUE})`,
      )
    : false;
  const hasInvalidValueQuantityFlag = hasQuantityFlag
    ? ((quantityFlagSerie?.data ?? []) as Highcharts.PointOptionsObject[]).some(
        s => s.marker?.symbol === `url(${MARKER_SVG_INVALID})`,
      )
    : false;
  const hasMissingValueAndInvalidQuantityFlag = hasQuantityFlag
    ? ((quantityFlagSerie?.data ?? []) as Highcharts.PointOptionsObject[]).some(
        s => s.marker?.symbol === `url(${MARKER_SVG_NO_VALUE_INVALID})`,
      )
    : false;

  const invalidQuantityFlagSerie = series.find(s => s.id === INVALID_QUANTITY_FLAG_SERIE_ID);
  const hasInvalidQuantityFlag =
    invalidQuantityFlagSerie && testSerieData(invalidQuantityFlagSerie, period, trueFun);

  const analysisFlagSerie = series.find(s => s.id === ANALYSIS_FLAGS_SERIE_ID);
  const sigmaSerie = series.find(s => s.id === SIGMA_AREA_SERIE_ID);
  const allDataFlagsSeriePresent = series.some(s => s.id === ALLDATA_FLAGS_SERIE_ID);

  const hasUncompletedSimulation = analysisFlagSerie
    ? hasFlag(analysisFlagSerie, "uncompletedSimulation", period)
    : false;
  const hasUncompletedDiagnosis = analysisFlagSerie
    ? hasFlag(analysisFlagSerie, "uncompletedDiagnosis", period)
    : false;
  const hasSimulationError = analysisFlagSerie
    ? hasFlag(analysisFlagSerie, "simulationError", period)
    : false;
  const hasDiagnosisError = analysisFlagSerie
    ? hasFlag(analysisFlagSerie, "diagnosisError", period)
    : false;
  const hasUnavailableSimulation = analysisFlagSerie
    ? hasFlag(analysisFlagSerie, "unavailableSimulation", period)
    : false;
  const hasSigmaPoint = sigmaSerie
    ? testSerieData(
        sigmaSerie,
        period,
        p => typeof p.low === "number" && typeof p.high === "number",
      )
    : false;

  const [publicationDisplayed] = usePublicationFlagDisplay({ scope: projectId });

  const hasAnalysesIncludedInReporting = React.useMemo(
    () => analyses.some(a => "isPublished" in a && a.isPublished),
    [analyses],
  );

  return (
    <>
      {markerEnabled ? (
        <>
          {hasQuantityFlag && hasMissingValueQuantityFlag && (
            <Marker
              description={t("flags_legend.quantity_flag.missing_value.description")}
              name={t("flags_legend.quantity_flag.missing_value")}
            >
              <ImgFlag src={MARKER_NO_VALUE} />
            </Marker>
          )}
          {hasQuantityFlag && hasInvalidValueQuantityFlag && (
            <Marker
              description={t("flags_legend.quantity_flag.invalid_value.description")}
              name={t("flags_legend.quantity_flag.invalid_value")}
            >
              <ImgFlag src={MARKER_SVG_INVALID} />
            </Marker>
          )}
          {hasQuantityFlag && hasMissingValueAndInvalidQuantityFlag && (
            <Marker
              description={t(
                "flags_legend.quantity_flag.missing_value_and_invalid_value.description",
              )}
              name={t("flags_legend.quantity_flag.missing_value_and_invalid_value")}
            >
              <ImgFlag src={MARKER_SVG_NO_VALUE_INVALID} />
            </Marker>
          )}
          {(chartType === "summary" || chartType === "diagnosis") && hasInvalidQuantityFlag ? (
            <Marker
              description={t("flags_legend.quantity_flag.invalid_value.description")}
              name={t("flags_legend.quantity_flag.invalid_value")}
            >
              <ImgFlag src={MARKER_SVG_INVALID} />
            </Marker>
          ) : null}
        </>
      ) : null}
      {hasSigmaPoint && (
        <Marker
          name={t("flags_legend.sigma")}
          description={
            chartType === "amplitude"
              ? t("flags_legend.sigma.amplitude.description")
              : t("flags_legend.sigma.gap.description")
          }
        >
          <SigmaZone />
        </Marker>
      )}
      {visibleGroups.length > 0 && (
        <Marker
          description={t("flags_legend.intercycles.description")}
          name={t("flags_legend.intercycles")}
        >
          <IntercycleZone />
        </Marker>
      )}
      {visibleNoDataIntervals.length > 0 && (
        <Marker
          description={t("flags_legend.noDataIntervals.description")}
          name={t("flags_legend.noDataIntervals")}
        >
          <Stripe />
        </Marker>
      )}
      {faultEventBandsAreVisible && (
        <Marker
          description={t("flags_legend.out_of_fault_event_period.description")}
          name={t("flags_legend.out_of_fault_event_period")}
        >
          <OutOfFaultEventZone />
        </Marker>
      )}
      {(hasUncompletedDiagnosis ?? hasUncompletedSimulation) && (
        <Marker
          description={t("flags_legend.uncomplete_simulation_diagnosis.description")}
          name={t("flags_legend.uncomplete_simulation_diagnosis")}
        >
          <Flag color={flagColors.info} />
        </Marker>
      )}
      {hasUnavailableSimulation && (
        <Marker
          description={t("flags_legend.unavailable_simulation.description")}
          name={t("flags_legend.unavailable_simulation")}
        >
          <Flag color={flagColors.warning} />
        </Marker>
      )}
      {(hasDiagnosisError || hasSimulationError) && (
        <Marker
          description={t("flags_legend.simulation_diagnosis_error.description")}
          name={t("flags_legend.simulation_diagnosis_error")}
        >
          <Flag color={flagColors.error} />
        </Marker>
      )}
      {markerEnabled && allDataFlagsSeriePresent ? (
        <Marker
          description={t("flags_legend.allData_activeAnalysis.description")}
          name={t("flags_legend.allData_activeAnalysis")}
        >
          <Flag color={flagColorsAllData.activeAnalysis} />
        </Marker>
      ) : null}
      {(chartType === "summary" || (publicationDisplayed && !allAnalysesMode)) &&
      hasAnalysesIncludedInReporting ? (
        <>
          <Marker
            description={t("flags_legend.published_analysis_in_reporting.description")}
            name={t("flags_legend.published_analysis_in_reporting")}
          >
            <Flag color={flagColors.publishedIncluded} stroke={flagLineColors.publishedIncluded} />
          </Marker>
          {chartType !== "summary" && (
            <Marker
              description={t("flags_legend.published_analysis_not_in_reporting.description")}
              name={t("flags_legend.published_analysis_not_in_reporting")}
            >
              <Flag
                color={flagColors.publishedNotIncluded}
                stroke={flagLineColors.publishedNotIncluded}
              />
            </Marker>
          )}
        </>
      ) : null}
    </>
  );
}
export default React.memo(CommonLegend);
